import { useEffect, useState } from "react"
import { VisitorFollowUpEdit } from "./VisitorFollowUpEdit"
import { VisitorApi } from "./visitor-api"
import { useFetcher, useParams, useNavigate } from "react-router-dom"
import { FollowUpDto, Member, VisitLog, Visitor, VisitPurpose, VisitorType, Profession, InformationSource, ContactPreference, FollowUpStatus } from "./visitor-dto"
import { isoToVerbalDate, isoToVerbalDateTime } from "../../utils/dateFunction"
import { VisitorEdit } from "./VisitorEdit"
import { path } from "../../navigation/path"


type Person = {
    name: string
    phone: string
    email: string
    date_of_birth: string
}
export const VisitorDetails = () => {

    const navigate = useNavigate()
    const [showAddFollowUpow, setShowAddFollowUp] = useState(false)
    const [editFollowUp, setEditFollowUp] = useState<FollowUpDto>()
    const [logs, setLogs] = useState<VisitLog[]>([])

    const params = useParams()

    const [member, setMember] = useState<Member>()
    const [visitor, setVisitor] = useState<Visitor>()
    const [person, setPerson] = useState<Person>()
    const [followUps, setFollowUps] = useState<FollowUpDto[]>([])

    const [showEditvisitor, setShowEditvisitor] = useState(false)

    useEffect(() => {
        if (params.type == "member_id")
            VisitorApi.getVisitLogsByMemberId(Number(params.id)).then(d => {
                const responseData = d?.data
                // API returns { status: "ok", data: VisitLog[] } or { success: true, data: VisitLog[] }
                if (responseData?.status === "ok" && responseData?.data) {
                    setLogs(responseData.data)
                } else if (responseData?.success && responseData?.data) {
                    setLogs(responseData.data)
                } else if (responseData?.data) {
                    // Fallback for old format
                    setLogs(responseData.data)
                }
            })
        else if (params.type == "visitor_id")
            VisitorApi.getVisitLogsByVisitorId(Number(params.id)).then(d => {
                const responseData = d?.data
                // API returns { status: "ok", data: VisitLog[] } or { success: true, data: VisitLog[] }
                if (responseData?.status === "ok" && responseData?.data) {
                    setLogs(responseData.data)
                } else if (responseData?.success && responseData?.data) {
                    setLogs(responseData.data)
                } else if (responseData?.data) {
                    // Fallback for old format
                    setLogs(responseData.data)
                }
            })
    }, [])

    useEffect(() => {
        const [firstLog] = logs

        if (firstLog) {
            if (firstLog.member)
                setMember(firstLog.member)
            else if (firstLog.visitor)
                setVisitor(firstLog.visitor)
        }

    }, [logs])

    useEffect(() => {
        if (member) {
            setPerson({
                name: member.name || '',
                phone: member.phone || '',
                date_of_birth: member.date_of_birth || '',
                email: ''
            })
        }
    }, [member])

    useEffect(() => {
        if (visitor) {
            const phone = visitor?.phone || (visitor?.phones && visitor.phones.length > 0 ? visitor.phones.join(', ') : 'N/A')
            const email = visitor?.email || (visitor?.emails && visitor.emails.length > 0 ? visitor.emails.join(', ') : 'N/A')
            
            setPerson({
                name: visitor?.visitor_full_name || '',
                phone: phone,
                date_of_birth: '',
                email: email
            })

            loadFollowUps(visitor.id)
        }
    }, [visitor])




    function loadFollowUps(visitorId: number) {

        VisitorApi.getFollowups(visitorId).then(d => {
            setFollowUps(d?.data.data)
        })
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
            <div className="max-w-[1600px] mx-auto">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate(path.VISITORS)}
                                className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                                title="Go back"
                            >
                                <i className="fa-solid fa-arrow-left"></i>
                            </button>
                    <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                    Visitor Details
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Complete visitor information and follow-up history
                                </p>
                            </div>
                        </div>
                        {visitor && (
                            <button 
                                className="btn-primary flex items-center gap-2"
                                onClick={() => setShowEditvisitor(true)}
                            >
                                <i className="fa-solid fa-user-edit"></i>
                                <span>Edit Visitor</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Visitor Profile Card */}
                {logs.length > 0 && logs[0].visitor && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
                        <div className="flex flex-col md:flex-row md:items-start gap-6">
                            <div className="flex-shrink-0">
                                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                                    {logs[0].visitor?.visitor_full_name?.charAt(0)?.toUpperCase() || 'V'}
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-4">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {logs[0].visitor?.visitor_full_name || 'Unknown Visitor'}
                                    </h2>
                                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold rounded-full">
                                        Visitor
                                    </span>
                                    {logs[0].visitor?.is_prospect && (
                                        <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-sm font-semibold rounded-full">
                                            Prospect
                                        </span>
                                    )}
                                </div>
                                
                                {/* Contact Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-phone text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Phone</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.phone 
                                                    ? logs[0].visitor.phone
                                                    : (logs[0].visitor?.phones && logs[0].visitor.phones.length > 0 
                                                        ? logs[0].visitor.phones.join(', ') 
                                                        : 'N/A')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-envelope text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Email</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.email 
                                                    ? logs[0].visitor.email
                                                    : (logs[0].visitor?.emails && logs[0].visitor.emails.length > 0 
                                                        ? logs[0].visitor.emails.join(', ') 
                                                        : 'N/A')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-briefcase text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Designation/Profession</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.designation_profession !== undefined && logs[0].visitor.designation_profession !== Profession.OTHER 
                                                    ? Profession[logs[0].visitor.designation_profession] 
                                                    : logs[0].visitor?.other_designation_profession || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-info-circle text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Source of Information</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.source_of_information !== undefined && logs[0].visitor.source_of_information !== InformationSource.OTHER
                                                    ? InformationSource[logs[0].visitor.source_of_information]
                                                    : logs[0].visitor?.other_source_specify || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-church text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Current Church</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.current_church || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-user-tie text-gray-600 dark:text-gray-400"></i>
                                            </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Church Pastor(s)</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.current_church_pastors 
                                                    ? (Array.isArray(logs[0].visitor.current_church_pastors) 
                                                        ? (logs[0].visitor.current_church_pastors.length > 0 ? logs[0].visitor.current_church_pastors.join(', ') : 'N/A')
                                                        : logs[0].visitor.current_church_pastors)
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-map-marker-alt text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Church Address</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.current_church_address || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <i className="fa-solid fa-handshake text-gray-600 dark:text-gray-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Contact Preference</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {logs[0].visitor?.contact_preference !== undefined 
                                                    ? ContactPreference[logs[0].visitor.contact_preference] 
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Prayer Request */}
                                {logs[0].visitor?.prayer_request && (
                                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <div className="flex items-start gap-3">
                                            <i className="fa-solid fa-pray text-blue-600 dark:text-blue-400 mt-1"></i>
                                            <div>
                                                <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-1 font-semibold">Prayer Request</p>
                                                <p className="text-sm text-blue-900 dark:text-blue-100">{logs[0].visitor.prayer_request}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Referrer Information */}
                                {(logs[0].visitor?.is_referrer_member || logs[0].visitor?.referrer_full_name || logs[0].visitor?.referrer_phone || logs[0].visitor?.referrer_address) && (
                                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <p className="text-xs text-green-700 dark:text-green-300 uppercase tracking-wide mb-3 font-semibold">Referrer Information</p>
                                        {logs[0].visitor?.is_referrer_member && logs[0].visitor?.referrer_member ? (
                                            (() => {
                                                const referrer = logs[0].visitor.referrer_member
                                                return (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-3">
                                                            {referrer?.image_path ? (
                                                                <img 
                                                                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:3070'}/${referrer.image_path}`}
                                                                    alt={referrer?.name || 'Referrer'}
                                                                    className="w-12 h-12 rounded-full object-cover border-2 border-green-300 dark:border-green-700"
                                                                    onError={(e) => {
                                                                        (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${referrer?.name || 'Member'}`
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-full bg-green-200 dark:bg-green-800 flex items-center justify-center text-green-700 dark:text-green-300 font-semibold">
                                                                    {referrer?.name?.charAt(0)?.toUpperCase() || 'M'}
                                                                </div>
                                                            )}
                                                            <div>
                                                                <p className="text-sm font-semibold text-green-900 dark:text-green-100">{referrer?.name || 'N/A'}</p>
                                                                <p className="text-xs text-green-700 dark:text-green-300">Member</p>
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-green-200 dark:border-green-700">
                                                            {referrer?.phone && (
                                                                <div>
                                                                    <p className="text-xs text-green-700 dark:text-green-300 uppercase tracking-wide mb-1">Phone</p>
                                                                    <p className="text-sm text-green-900 dark:text-green-100">{referrer.phone}</p>
                                                                </div>
                                                            )}
                                                            {referrer?.address && (
                                                                <div>
                                                                    <p className="text-xs text-green-700 dark:text-green-300 uppercase tracking-wide mb-1">Address</p>
                                                                    <p className="text-sm text-green-900 dark:text-green-100">{referrer.address}</p>
                                                                </div>
                                                            )}
                                                            {referrer?.member_type && (
                                                                <div>
                                                                    <p className="text-xs text-green-700 dark:text-green-300 uppercase tracking-wide mb-1">Member Type</p>
                                                                    <p className="text-sm text-green-900 dark:text-green-100 capitalize">{referrer.member_type}</p>
                                                                </div>
                                                            )}
                                                            {referrer?.date_of_birth && (
                                                                <div>
                                                                    <p className="text-xs text-green-700 dark:text-green-300 uppercase tracking-wide mb-1">Date of Birth</p>
                                                                    <p className="text-sm text-green-900 dark:text-green-100">{isoToVerbalDate(referrer.date_of_birth)}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })()
                                        ) : (
                                            <div className="space-y-2">
                                                {logs[0].visitor?.referrer_full_name && (
                                                    <p className="text-sm text-green-900 dark:text-green-100">
                                                        <span className="font-medium">Name:</span> {logs[0].visitor.referrer_full_name}
                                                    </p>
                                                )}
                                                {logs[0].visitor?.referrer_phone && (
                                                    <p className="text-sm text-green-900 dark:text-green-100">
                                                        <span className="font-medium">Phone:</span> {logs[0].visitor.referrer_phone}
                                                    </p>
                                                )}
                                                {logs[0].visitor?.referrer_address && (
                                                    <p className="text-sm text-green-900 dark:text-green-100">
                                                        <span className="font-medium">Address:</span> {logs[0].visitor.referrer_address}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Visit History Section */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                        <i className="fa-solid fa-calendar-check text-blue-600 dark:text-blue-400"></i>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Visit History</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{logs.length} total visits</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {logs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                                            <i className="fa-solid fa-calendar-xmark text-2xl text-gray-400 dark:text-gray-500"></i>
                                        </div>
                                        <p className="text-gray-500 dark:text-gray-400">No visit history available</p>
                                    </div>
                                ) : (
                                    logs.map((l, index) => (
                                        <div 
                                            key={index}
                                            className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-800/50 rounded-lg border-l-4 border-blue-500 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <i className="fa-solid fa-clock text-gray-500 dark:text-gray-400 text-sm"></i>
                                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            Entry: {l.entry_time ? isoToVerbalDateTime(l.entry_time) : (l.check_in ? isoToVerbalDateTime(l.check_in) : 'N/A')}
                                                        </p>
                                                    </div>
                                                    {l.exit_time && (
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <i className="fa-solid fa-sign-out text-gray-500 dark:text-gray-400 text-sm"></i>
                                                            <p className="text-sm text-gray-900 dark:text-white">
                                                                Exit: {isoToVerbalDateTime(l.exit_time)}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {!l.exit_time && l.check_out && (
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <i className="fa-solid fa-sign-out text-gray-500 dark:text-gray-400 text-sm"></i>
                                                            <p className="text-sm text-gray-900 dark:text-white">
                                                                Check-out: {isoToVerbalDateTime(l.check_out)}
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded">
                                                            {VisitPurpose[l.purpose] || 'N/A'}
                                                        </span>
                                                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded">
                                                            {VisitorType[l.visitory_type] || 'N/A'}
                                                        </span>
                                                    </div>
                                                    {l.notes && (
                                                        <div className="mt-2">
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes:</p>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300">{l.notes}</p>
                                                        </div>
                                                    )}
                                                    {l.to_meet && l.to_meet.length > 0 && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">To Meet:</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {l.to_meet.map((member, idx) => (
                                                                    <span key={idx} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded">
                                                                        {member.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {l.vehicles && l.vehicles.length > 0 && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Vehicles:</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {l.vehicles.map((vehicle: any, idx: number) => (
                                                                    <div key={idx} className="px-3 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800">
                                                                        <div className="flex items-center gap-2">
                                                                            <i className="fa-solid fa-car text-orange-600 dark:text-orange-400 text-xs"></i>
                                                                            <div>
                                                                                <p className="text-xs font-semibold text-orange-900 dark:text-orange-100">{vehicle.reg_no || 'N/A'}</p>
                                                                                {vehicle.type && (
                                                                                    <p className="text-xs text-orange-700 dark:text-orange-300 capitalize">{vehicle.type}</p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(l as any).require_follow_up && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                            <div className="flex items-center gap-2">
                                                                <i className="fa-solid fa-flag text-red-500 dark:text-red-400 text-sm"></i>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Follow-up Required</p>
                                                                {(l as any).follow_up_status !== null && (l as any).follow_up_status !== undefined && (
                                                                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium rounded">
                                                                        {FollowUpStatus[(l as any).follow_up_status]?.replace(/_/g, ' ') || 'N/A'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    {l.exit_time || l.check_out ? (
                                                        <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full flex items-center gap-1">
                                                            <i className="fa-solid fa-check-circle"></i>
                                                            Checked Out
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs font-semibold rounded-full flex items-center gap-1">
                                                            <i className="fa-solid fa-circle"></i>
                                                            In Progress
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats Sidebar */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Stats</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                            <i className="fa-solid fa-calendar-days text-blue-600 dark:text-blue-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Total Visits</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">{logs.length}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                            <i className="fa-solid fa-clipboard-check text-green-600 dark:text-green-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Follow-ups</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">{followUps.length}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                            <i className="fa-solid fa-clock text-purple-600 dark:text-purple-400"></i>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Active Visits</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                {logs.filter(l => !l.exit_time).length}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Follow-ups Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mt-6">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                <i className="fa-solid fa-history text-purple-600 dark:text-purple-400"></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Follow-ups</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{followUps.length} total follow-ups</p>
                            </div>
                        </div>
                        {visitor && (
                            <button 
                                className="btn-primary flex items-center gap-2"
                                onClick={() => setShowAddFollowUp(true)}
                            >
                                <i className="fa-solid fa-plus"></i>
                                <span>Add Follow-up</span>
                            </button>
                        )}
                        </div>

                        <div className="overflow-x-auto">
                        {followUps.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                                    <i className="fa-solid fa-clipboard-list text-2xl text-gray-400 dark:text-gray-500"></i>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 mb-4">No follow-ups recorded</p>
                                {visitor && (
                                    <button 
                                        className="btn-primary flex items-center gap-2 mx-auto"
                                        onClick={() => setShowAddFollowUp(true)}
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                        <span>Add First Follow-up</span>
                                    </button>
                                )}
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact Method</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Next Follow-up</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {followUps.map((f, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <i className="fa-solid fa-calendar text-gray-400 dark:text-gray-500 text-sm"></i>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {f.contacted_date ? isoToVerbalDateTime(f.contacted_date) : 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                    <i className="fa-solid fa-phone text-gray-600 dark:text-gray-400"></i>
                                                    <span className="text-sm text-gray-900 dark:text-white">Phone</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                                <p className="text-sm text-gray-900 dark:text-white max-w-xs truncate">{f.notes || 'No notes'}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                    {f.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {f.next_proposed_contact_date ? isoToVerbalDateTime(f.next_proposed_contact_date) : 'Not scheduled'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <button 
                                                    className="btn-primary text-sm"
                                                    onClick={() => setEditFollowUp(f)}
                                                >
                                                    <i className="fa-solid fa-edit mr-1"></i>
                                                    Edit
                                                </button>
                                        </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showAddFollowUpow && visitor && (
                <VisitorFollowUpEdit 
                    visitor={visitor} 
                    onClose={() => setShowAddFollowUp(false)} 
                    onSuccess={() => {
            if (visitor) {
                loadFollowUps(visitor.id)
            }
            setShowAddFollowUp(false)
                    }} 
                />
            )}

            {editFollowUp && visitor && (
                <VisitorFollowUpEdit 
                    followUp={editFollowUp} 
                    visitor={visitor} 
                    onClose={() => setEditFollowUp(undefined)} 
                    onSuccess={() => {
            if (visitor) {
                loadFollowUps(visitor.id)
            }
            setEditFollowUp(undefined)
                    }} 
                />
            )}

            {showEditvisitor && visitor && (
                <VisitorEdit 
                    visitor={visitor} 
                    onClose={() => setShowEditvisitor(false)} 
                    onSuccess={() => {
                setShowEditvisitor(false)
                    }} 
                />
            )}
        </div>
    )
}
